import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { unitsApi } from '@/api';
import { ProductCatalogNav } from '@/components/org/ProductCatalogNav';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import type { UnitRequest, UnitResponse } from '@/types/product';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const empty: UnitRequest = { name: '', symbol: '' };

export function UnitsPage() {
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<UnitResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<UnitResponse | null>(null);
  const [form, setForm] = useState<UnitRequest>(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await unitsApi.list({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
        activeOnly: false,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load units');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: Column<UnitResponse>[] = [
    { key: 'name', header: 'Unit' },
    { key: 'symbol', header: 'Symbol', render: (r) => r.symbol ?? '—' },
    { key: 'status', header: 'Status', render: (r) => (r.status ? 'Active' : 'Inactive') },
    {
      key: 'edit',
      header: '',
      render: (r) => (
        <button
          type="button"
          className="text-primary-600"
          onClick={() => {
            setEditing(r);
            setForm({ name: r.name, symbol: r.symbol });
            setModalOpen(true);
          }}
        >
          Edit
        </button>
      ),
    },
  ];

  const save = async () => {
    if (!form.name.trim()) {
      notify.error('Unit name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) await unitsApi.update(editing.id, form);
      else await unitsApi.create(form);
      notify.success('Saved');
      setModalOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Units"
        breadcrumbs={[
          { label: 'POS', to: '/pos/dashboard' },
          { label: 'Products', to: '/pos/products' },
          { label: 'Units' },
        ]}
        actions={
          <Button
            onClick={() => {
              setEditing(null);
              setForm(empty);
              setModalOpen(true);
            }}
          >
            <AddIcon fontSize="small" /> Add unit
          </Button>
        }
      />
      <ProductCatalogNav />
      <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
        Units of measure (e.g. Piece, Kg, Litre) appear in the product form dropdown.
      </p>
      <DataTable columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => {
          pagination.setSearch(v);
          pagination.resetPage();
        }}
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
      />
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit unit' : 'New unit'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} loading={saving}>
              Save
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Unit name *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Piece"
            autoFocus
          />
          <Input
            label="Symbol"
            value={form.symbol ?? ''}
            onChange={(e) => setForm({ ...form, symbol: e.target.value })}
            placeholder="e.g. pcs"
          />
        </div>
      </Modal>
    </div>
  );
}
